import { graphHooks } from '@tunasong/graph-lib/react'
import { useRecentSplit } from '@tunasong/search'
import { useFavorites } from '@tunasong/ui-lib'
import { mergeContinueItems } from './continue-items.js'

/**
 * What Home's Continue shelf shows: pinned entities (in pin order), then the entities I last edited
 * (`useRecentSplit().mine`), deduped and capped. `isEmpty` is the first-run state, settled only once loaded.
 */
export const useContinueItems = () => {
  const { mine, isLoading: isLoadingSplit, isSuccess: isSplitResolved, isError, retry } = useRecentSplit()
  const { pinnedFavorites } = useFavorites()

  const pinnedIds = pinnedFavorites.map(f => f.entityId).filter((id): id is string => typeof id === 'string')
  const mineIds = mine.map(e => e.id)
  const { existingEntities = [], isLoading: isResolving } = graphHooks.useEntitiesById([
    ...new Set([...pinnedIds, ...mineIds]),
  ])
  const items = mergeContinueItems({
    pinnedIds,
    mineIds,
    entitiesById: new Map(existingEntities.map(e => [e.id, e])),
  })

  /**
   * The feed's query is held back briefly and is skipped, not loading, until then: that reads as loading here, or
   * a user with content sees the first-run state flash (GH #2914). Empty only after a resolved empty feed.
   * A failed feed is neither: it shows its error and whatever pins resolved (GH #2915).
   */
  const loading = ((isLoadingSplit || !isSplitResolved) && !isError) || isResolving
  return { items, loading, isEmpty: !loading && !isError && items.length === 0, isError, retry }
}
